import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { auth, googleProvider, isFirebaseConfigured } from '@/firebase';
import { signInWithPopup } from 'firebase/auth';
import { getStoredMode, modeToRoute } from '@/components/ModeToggle';

const GoogleIcon = () => (
  <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
    <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ setToken, setUser }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showAuth, setShowAuth] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoadingAction(isSignup ? 'signup' : 'login');

    try {
      const endpoint = isSignup ? '/auth/signup' : '/auth/login';
      const data = isSignup ? formData : { email: formData.email, password: formData.password };
      
      const response = await axios.post(`${API}${endpoint}`, data);
      if (response.data.unverified) {
        toast.success(response.data.message || 'Signup successful. Please check your email to verify your account.');
        setIsSignup(false);
        return;
      }
      setToken(response.data.token);
      setUser(response.data.user);
      toast.success(t(isSignup ? 'toast.welcome.signup' : 'toast.welcome.login'));
      // If there's a pending invitation token (user clicked an invite link before signing in),
      // resume the accept flow now that they have a session.
      let pendingInvite = null;
      try { pendingInvite = localStorage.getItem('align_pending_invite_token'); } catch (_) {}
      if (pendingInvite) {
        navigate(`/invite/${pendingInvite}`);
        return;
      }
      // Otherwise return user to whichever mode they last chose (Personal / Corporate / Family).
      // New users default to Personal.
      navigate(modeToRoute(getStoredMode()));
    } catch (error) {
      // 403 on /auth/login means email verification is required. Offer a resend
      // path right from the toast so locked-out users have a recovery option.
      if (!isSignup && error.response?.status === 403 && formData.email) {
        toast.error(error.response?.data?.detail || t('auth.verify.needed', 'Please verify your email address before logging in.'), {
          action: {
            label: t('auth.verify.resend', 'Resend email'),
            onClick: () => handleResendVerification(formData.email),
          },
          duration: 10000,
        });
      } else {
        toast.error(error.response?.data?.detail || t('toast.auth.error'));
      }
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  const handleResendVerification = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success(response.data?.message || t('auth.verify.resentBody', 'If an account exists for that email, a new verification link has been sent.'));
    } catch (_) {
      toast.error(t('toast.auth.error'));
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isFirebaseConfigured || !auth || !googleProvider) {
      toast.error(t('toast.auth.error'));
      return;
    }
    setLoading(true);
    setLoadingAction('google');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      
      const response = await axios.post(`${API}/auth/google`, {
        id_token: idToken,
        api_key: process.env.REACT_APP_FIREBASE_API_KEY
      });
      setToken(response.data.token);
      setUser(response.data.user);
      toast.success(t('toast.welcome.login'));
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || t('toast.auth.error'));
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  return (
    <div className="hero-section flex items-center justify-center p-4 sm:p-8">
      <img
        src="https://images.unsplash.com/photo-1578258691902-327e3c08b7e9?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="Foggy Forest"
        className="hero-bg"
      />
      
      {/* Language Switcher & About Link */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-3">
        <Button
          onClick={() => navigate('/about')}
          variant="ghost"
          className="rounded-full bg-background/20 backdrop-blur-sm border border-border/40 hover:bg-background/40 text-foreground text-xs sm:text-sm h-9 px-4 transition-all duration-300"
          data-testid="about-landing-link"
        >
          {t('nav.about')}
        </Button>
        <LanguageSwitcher />
      </div>
      
      <div className="relative z-10 max-w-6xl w-full mx-auto">
        {!showAuth ? (
          <div className="text-center space-y-8 sm:space-y-12 fade-in px-4">
            <div className="space-y-4 sm:space-y-6">
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight text-foreground" data-testid="landing-hero-title">
                {t('landing.title')}
              </h1>
              <p className="font-serif text-xl sm:text-2xl lg:text-3xl text-muted-foreground max-w-2xl mx-auto" data-testid="landing-tagline">
                {t('landing.tagline')}
              </p>
            </div>
            
            <div className="space-y-6 max-w-xl mx-auto">
              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
                <Trans i18nKey="landing.description" components={{ 1: <span className="font-semibold" />, 3: <span className="font-semibold" />, 5: <span className="font-semibold" /> }} />
              </p>
              
              <Button
                onClick={() => setShowAuth(true)}
                className="rounded-full px-8 sm:px-12 py-5 sm:py-6 text-sm sm:text-base font-medium bg-primary hover:bg-primary/90 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                data-testid="get-started-btn"
              >
                {t('landing.cta')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="max-w-md mx-auto animate-in px-4">
            <div className="glass-card rounded-2xl p-6 sm:p-10 shadow-2xl border border-border/50">
              <h2 className="font-serif text-3xl sm:text-4xl font-light text-center mb-6 sm:mb-8" data-testid="auth-title">
                {t(isSignup ? 'auth.signup' : 'auth.login')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-testid="auth-form">
                {isSignup && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">{t('auth.name')}</label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-transparent border-b border-border focus:border-primary rounded-none px-0 font-serif text-base sm:text-lg"
                      required
                      data-testid="signup-name-input"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">{t('auth.email')}</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-transparent border-b border-border focus:border-primary rounded-none px-0 font-serif text-base sm:text-lg"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">{t('auth.password')}</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-transparent border-b border-border focus:border-primary rounded-none px-0 font-serif text-base sm:text-lg pr-10"
                      required
                      data-testid="auth-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full py-5 sm:py-6 text-sm sm:text-base font-medium bg-primary hover:bg-primary/90 transition-all duration-300 hover:scale-105 active:scale-95"
                  data-testid="auth-submit-btn"
                >
                  {loadingAction === 'signup' || loadingAction === 'login' ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {loadingAction === 'signup'
                        ? t('auth.loading.signup', 'Creating Account...')
                        : t('auth.loading.login', 'Signing In...')}
                    </span>
                  ) : (
                    t(isSignup ? 'auth.submit.signup' : 'auth.submit.login')
                  )}
                </Button>
              </form>

              {isFirebaseConfigured && (
                <>
                  <div className="relative my-4 flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border/80"></div>
                    </div>
                    <span className="relative bg-[#1a1512] px-3 text-xs text-muted-foreground uppercase">{t('auth.or')}</span>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full rounded-full py-5 sm:py-6 text-sm sm:text-base font-medium border border-border/80 bg-transparent hover:bg-muted/10 transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center"
                    data-testid="google-signin-btn"
                  >
                    {loadingAction === 'google' ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('auth.loading.google', 'Connecting...')}
                      </span>
                    ) : (
                      <>
                        <GoogleIcon />
                        {t('auth.google')}
                      </>
                    )}
                  </Button>
                </>
              )}
              
              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsSignup(!isSignup)}
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  {t(isSignup ? 'auth.toggle.signup' : 'auth.toggle.login')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
